import React from 'react';
import { GameTipPanel, GameTipHost, statsToText } from './GameTip';
import './GoldenEffectBanner.css';

/* ────────────────────────────────────────────
   Effect icon map — consolidated from all pages
   ──────────────────────────────────────────── */
const EFFECT_ICON_MAP = {
  double_edge: 'sword',
  streak_shield: 'shield',
  emergency_revive: 'revive',
  revive: 'revive',
  mild_analgesic: 'shield',
  anxiolytic_tolerance: 'shield',
  altered_perception: 'hourglass',
  stimulant_session: 'zap',
  neural_rewire: 'mushroom',
  lifesteal: 'droplet',
  second_wind: 'wind',
  emergency_reserves: 'flame',
  basal_shield: 'heart_shield',
  bolus_guard: 'heart_shield',
  cocky: 'crown',
};

const IconSvg = ({ children }) => (
  <svg className="geb-icon" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const EffectIcons = {
  sword: () => (
    <IconSvg>
      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
      <line x1="13" y1="19" x2="19" y2="13" />
      <line x1="16" y1="16" x2="20" y2="20" />
      <line x1="19" y1="21" x2="21" y2="19" />
    </IconSvg>
  ),
  shield: () => (
    <IconSvg>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </IconSvg>
  ),
  revive: () => (
    <IconSvg>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
      <polyline points="8 13 12 9 16 13" />
    </IconSvg>
  ),
  crosshair: () => (
    <IconSvg>
      <circle cx="12" cy="12" r="10" />
      <line x1="22" y1="12" x2="18" y2="12" />
      <line x1="6" y1="12" x2="2" y2="12" />
      <line x1="12" y1="6" x2="12" y2="2" />
      <line x1="12" y1="22" x2="12" y2="18" />
    </IconSvg>
  ),
  magnet: () => (
    <IconSvg>
      <path d="M6 2v6a6 6 0 0 0 12 0V2" />
      <line x1="6" y1="2" x2="6" y2="6" />
      <line x1="18" y1="2" x2="18" y2="6" />
      <rect x="3" y="1" width="6" height="4" rx="1" />
      <rect x="15" y="1" width="6" height="4" rx="1" />
    </IconSvg>
  ),
  hourglass: () => (
    <IconSvg>
      <path d="M6 2h12M6 22h12M7 2v4l5 6-5 6v4M17 2v4l-5 6 5 6v4" />
    </IconSvg>
  ),
  zap: () => (
    <IconSvg>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </IconSvg>
  ),
  mushroom: () => (
    <IconSvg>
      <path d="M12 2C6.5 2 2 6.5 2 12h20c0-5.5-4.5-10-10-10z" />
      <path d="M10 12v8M14 12v8M8 20h8" />
    </IconSvg>
  ),
  droplet: () => (
    <IconSvg>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </IconSvg>
  ),
  wind: () => (
    <IconSvg>
      <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
    </IconSvg>
  ),
  flame: () => (
    <IconSvg>
      <path d="M12 22c-4-3-8-7.5-8-12a8 8 0 0 1 16 0c0 4.5-4 9-8 12z" />
      <path d="M12 22c2-2 4-4.5 4-7a4 4 0 0 0-8 0c0 2.5 2 5 4 7z" />
    </IconSvg>
  ),
  heart_shield: () => (
    <IconSvg>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M12 9l1.5 1.5L12 12l-1.5-1.5z" strokeWidth="0" fill="currentColor" />
    </IconSvg>
  ),
  crown: () => (
    <IconSvg>
      <path d="M2 20h20L19 8l-4 5-3-7-3 7-4-5z" />
      <rect x="2" y="20" width="20" height="2" rx="1" />
    </IconSvg>
  ),
  default: () => (
    <IconSvg>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </IconSvg>
  ),
};

function getEffectIcon(effectId) {
  const iconKey = EFFECT_ICON_MAP[effectId] || 'default';
  return (EffectIcons[iconKey] || EffectIcons.default)();
}

/* ────────────────────────────────────────────
   Mechanic decode lines (tooltip canon grammar:
   <=7 words/line, no periods, StatRow stats;
   see wiki/decisions/2026-06-11-tooltip-canon.md)
   ──────────────────────────────────────────── */
const MECHANIC_ACCENT = {
  Tolerance: '#eab308',
  Comedown: '#f87171',
  Tachyphylaxis: '#fb923c',
};

function mechanicTipLines(mechanic, effect) {
  if (mechanic === 'Tolerance') {
    const floor = effect?.tolerance?.floor_mult;
    return [
      'repeat use weakens effect',
      floor != null && { stats: [{ j: 'floor' }, { t: `${Math.round(floor * 100)}% potency` }] },
    ].filter(Boolean);
  }
  if (mechanic === 'Comedown') {
    const dur = effect?.comedown?.duration_hours;
    const intensity = effect?.comedown?.base_intensity;
    return [
      'debuff when buff expires',
      (dur != null || intensity != null) && {
        stats: [
          dur != null && { g: 'rounds', v: `${dur}h` },
          intensity != null && { j: 'intensity' },
          intensity != null && { t: String(intensity) },
        ].filter(Boolean),
      },
    ].filter(Boolean);
  }
  // Tachyphylaxis
  const cooldown = effect?.params?.cooldown_hours;
  return [
    'tolerance builds fast',
    cooldown
      ? { stats: [{ g: 'rounds', v: `${cooldown}h` }, { j: 'cooldown between uses' }] }
      : { stats: [{ g: 'rounds' }, { j: 'needs cooldown between uses' }] },
  ];
}

/* Flatten the decode lines for the host aria-label. */
function mechanicAria(mechanic, lines) {
  const parts = [mechanic];
  lines.forEach((line) => {
    if (line && typeof line === 'object') {
      const text = statsToText(line.stats);
      if (text) parts.push(text);
    } else if (String(line || '').trim()) {
      parts.push(line);
    }
  });
  return parts.join(', ');
}

/* ────────────────────────────────────────────
   MechanicTag: GameTip-hosted mechanic decode
   ──────────────────────────────────────────── */
function MechanicTag({ mechanic, effect }) {
  const lines = mechanicTipLines(mechanic, effect);
  return (
    <GameTipHost
      as="span"
      className="geb-mechanic-tag combat-tt-host"
      style={{ '--effect-color': MECHANIC_ACCENT[mechanic] }}
      tabIndex={0}
      role="img"
      aria-label={mechanicAria(mechanic, lines)}
    >
      {mechanic}
      <GameTipPanel name={mechanic} sections={[{ lines }]} placement="above-left" />
    </GameTipHost>
  );
}

/* ────────────────────────────────────────────
   Shared: compute mechanics + filtered cons
   ──────────────────────────────────────────── */
function computeEffectData(effect) {
  const pros = effect?.pros || [];
  const cons = effect?.cons || [];

  const mechanics = [];
  if (effect?.tolerance?.enabled) mechanics.push('Tolerance');
  if (effect?.comedown?.enabled) mechanics.push('Comedown');
  if (effect?.params?.cooldown_hours > 0 && !effect?.tolerance?.enabled) {
    mechanics.push('Tachyphylaxis');
  }

  const mechanicNames = new Set(mechanics);
  const filteredCons = cons.filter(c => !mechanicNames.has(c));

  const consMechanics = cons.filter(c =>
    c === 'Tolerance' || c === 'Comedown' || c === 'Tachyphylaxis'
  );
  consMechanics.forEach(m => {
    if (!mechanicNames.has(m)) {
      mechanics.push(m);
      mechanicNames.add(m);
    }
  });

  return { pros, filteredCons, mechanics, isConsumable: effect?.type === 'consumable' };
}

/* ────────────────────────────────────────────
   Full banner content (default banner variant;
   the pill variant decodes via GameTip instead)
   ──────────────────────────────────────────── */
function BannerContent({ effect, isConsumable, pros, filteredCons, mechanics }) {
  const neutralTags = effect?.neutral || [];
  return (
    <>
      <div className="geb-header">
        {getEffectIcon(effect.id)}
        <span className="geb-name">{effect.name}</span>
        {isConsumable && <span className="geb-consumable-badge">Consumable</span>}
      </div>
      <p className="geb-desc">{effect.description}</p>

      {(pros.length > 0 || filteredCons.length > 0 || neutralTags.length > 0) && (
        <div className="geb-tags">
          {pros.map((pro, i) => (
            <span key={`pro-${i}`} className="geb-tag geb-tag--pro">{pro}</span>
          ))}
          {filteredCons.map((con, i) => (
            <span key={`con-${i}`} className="geb-tag geb-tag--con">{con}</span>
          ))}
          {neutralTags.map((tag, i) => (
            <span key={`neutral-${i}`} className="geb-tag geb-tag--neutral">{tag}</span>
          ))}
        </div>
      )}

      {mechanics.length > 0 && (
        <div className="geb-mechanics">
          {mechanics.map(m => (
            <MechanicTag key={m} mechanic={m} effect={effect} />
          ))}
        </div>
      )}
    </>
  );
}

/* ────────────────────────────────────────────
   GoldenEffectBanner
   Props: { effect, variant }
   variant: "banner" (default) | "pill"
   ──────────────────────────────────────────── */
/* ────────────────────────────────────────────
   MechanicPills — separate row of mechanic pills
   shown below a card. Each pill is hoverable with
   a tooltip showing details.
   ──────────────────────────────────────────── */
export function MechanicPills({ effect }) {
  if (!effect) return null;

  const { mechanics } = computeEffectData(effect);
  if (mechanics.length === 0) return null;

  return (
    <div className="geb-mechanic-pills">
      {mechanics.map(m => (
        <MechanicPill key={m} mechanic={m} effect={effect} />
      ))}
    </div>
  );
}

function MechanicPill({ mechanic, effect }) {
  const lines = mechanicTipLines(mechanic, effect);
  const iconKey = mechanic === 'Tolerance' ? 'tolerance'
    : mechanic === 'Comedown' ? 'comedown'
    : 'tachyphylaxis';

  return (
    <GameTipHost
      as="span"
      className={`geb-mpill geb-mpill--${iconKey} combat-tt-host`}
      style={{ '--effect-color': MECHANIC_ACCENT[mechanic] }}
      tabIndex={0}
      role="img"
      aria-label={mechanicAria(mechanic, lines)}
    >
      <MechanicPillIcon type={iconKey} />
      <span className="geb-mpill-label">{mechanic}</span>
      <GameTipPanel name={mechanic} sections={[{ lines }]} placement="above-left" />
    </GameTipHost>
  );
}

function MechanicPillIcon({ type }) {
  const size = 11;
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'tolerance') {
    return <svg {...props}><path d="M12 2v10l4 4" /><circle cx="12" cy="12" r="10" /></svg>;
  }
  if (type === 'comedown') {
    return <svg {...props}><path d="M18 6L6 18" /><polyline points="6 10 6 18 14 18" /></svg>;
  }
  // tachyphylaxis — lightning with slash
  return <svg {...props}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /><line x1="4" y1="4" x2="20" y2="20" /></svg>;
}

export { EFFECT_ICON_MAP, EffectIcons, getEffectIcon, computeEffectData };

export default function GoldenEffectBanner({ effect, variant = 'banner' }) {
  if (!effect) return null;

  const data = computeEffectData(effect);

  if (variant === 'pill') {
    return <PillVariant effect={effect} {...data} />;
  }

  const bannerClass = `geb-banner${data.isConsumable ? ' geb-banner--consumable' : ''}`;

  return (
    <div className={bannerClass}>
      <BannerContent effect={effect} {...data} />
    </div>
  );
}

/* ────────────────────────────────────────────
   Pill variant: compact pill, GameTip decode
   panel (hover/focus reveal, tap-toggle close)
   ──────────────────────────────────────────── */
function PillVariant({ effect, isConsumable, pros, filteredCons, mechanics }) {
  const neutralTags = effect?.neutral || [];

  // Decode sections: prose description falls back through the status-word
  // glyph pass; mechanics render FLAT (a tooltip never nests a tooltip).
  const sections = [];
  if (String(effect?.description || '').trim()) {
    sections.push({ lines: [effect.description] });
  }
  if (pros.length > 0) sections.push({ label: 'Pros', lines: pros });
  if (filteredCons.length > 0) sections.push({ label: 'Cons', lines: filteredCons });
  if (neutralTags.length > 0) sections.push({ label: 'Traits', lines: neutralTags });
  mechanics.forEach((m) => {
    sections.push({ label: m, lines: mechanicTipLines(m, effect) });
  });

  const ariaParts = [effect.name];
  if (isConsumable) ariaParts.push('Consumable');
  sections.forEach((s) => {
    if (s.label) ariaParts.push(s.label);
    s.lines.forEach((line) => {
      if (line && typeof line === 'object') {
        const text = statsToText(line.stats);
        if (text) ariaParts.push(text);
      } else if (String(line || '').trim()) {
        ariaParts.push(line);
      }
    });
  });

  const pillClass = `geb-pill${isConsumable ? ' geb-pill--consumable' : ''}`;

  return (
    <GameTipHost
      as="button"
      type="button"
      className={`${pillClass} combat-tt-host`}
      style={{ '--effect-color': isConsumable ? '#a855f7' : '#fbbf24' }}
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaParts.join(', ')}
    >
      {getEffectIcon(effect.id)}
      <span className="geb-pill-name">{effect.name}</span>
      <svg className="geb-pill-chevron" width="10" height="10" viewBox="0 0 16 16"
        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4 6l4 4 4-4" />
      </svg>
      <GameTipPanel
        placement="above-left"
        name={effect.name}
        subtitle={isConsumable ? 'Consumable' : null}
        icon={getEffectIcon(effect.id)}
        sections={sections}
      />
    </GameTipHost>
  );
}
